import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import {
  LightbulbIcon,
  CodeIcon,
  BrainCircuitIcon,
  NetworkIcon,
  BarChart4Icon,
  ShieldCheckIcon,
} from "lucide-react";
import "./Services.css"; // Import the CSS file

const Services = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();

  const services = [
    {
      icon: LightbulbIcon,
      titleEn: "Consultation & Strategy",
      titleFr: "Consultation & Stratégie",
      points: {
        en: [
          "Digital transformation roadmap",
          "Process optimization analysis",
          "Technology stack assessment",
          "ROI & feasibility studies",
        ],
        fr: [
          "Feuille de route de transformation digitale",
          "Analyse d'optimisation des processus",
          "Évaluation de la pile technologique",
          "Études de rentabilité et de faisabilité",
        ],
      },
      imageUrl:
        "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=400&q=80",
    },
    {
      icon: CodeIcon,
      titleEn: "Software Development",
      titleFr: "Développement Logiciel",
      points: {
        en: [
          "Custom software solutions",
          "Web & mobile applications",
          "API development & integration",
          "Legacy system modernization",
        ],
        fr: [
          "Solutions logicielles sur mesure",
          "Applications web et mobiles",
          "Développement et intégration d'API",
          "Modernisation des systèmes existants",
        ],
      },
      imageUrl:
        "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=400&q=80",
    },
    {
      icon: BrainCircuitIcon,
      titleEn: "Artificial Intelligence",
      titleFr: "Intelligence Artificielle",
      points: {
        en: [
          "Machine learning solutions",
          "Natural language processing",
          "Predictive analytics",
          "AI-powered automation",
        ],
        fr: [
          "Solutions d'apprentissage automatique",
          "Traitement du langage naturel",
          "Analyses prédictives",
          "Automatisation par IA",
        ],
      },
      imageUrl:
        "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=400&q=80",
    },
    {
      icon: NetworkIcon,
      titleEn: "System Integration",
      titleFr: "Intégration de Systèmes",
      points: {
        en: [
          "Seamless system connectivity",
          "Cloud migration services",
          "Enterprise architecture design",
          "Middleware solutions",
        ],
        fr: [
          "Connectivité transparente des systèmes",
          "Services de migration cloud",
          "Conception d'architecture d'entreprise",
          "Solutions de middleware",
        ],
      },
      imageUrl:
        "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=400&q=80",
    },
    {
      icon: BarChart4Icon,
      titleEn: "Data Analysis",
      titleFr: "Analyse de Données",
      points: {
        en: [
          "Interactive dashboards",
          "Real-time reporting",
          "Business intelligence",
          "Data visualization",
        ],
        fr: [
          "Tableaux de bord interactifs",
          "Rapports en temps réel",
          "Intelligence d'affaires",
          "Visualisation des données",
        ],
      },
      imageUrl:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=400&q=80",
    },
    {
      icon: ShieldCheckIcon,
      titleEn: "Automation",
      titleFr: "Automatisation",
      points: {
        en: [
          "Workflow automation",
          "Process automation and RPA",
          "Automated testing solutions",
          "End-to-end operational efficiency",
        ],
        fr: [
          "Automatisation des flux de travail",
          "Automatisation des processus et RPA",
          "Solutions de tests automatisés",
          "Efficacité opérationnelle de bout en bout",
        ],
      },
      imageUrl:
        "https://bairesdev.mo.cloudinary.net/blog/2023/10/Process-Automation-and-Software-Development.jpg?tx=w_1920,q_auto",
    },
  ];

  return (
    <section
      id="services"
      className={"py-24 px-4"}
    >

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-32">
        <h2
          className={`
            text-5xl 
            font-bold 
            ${theme === "dark"
              ? "bg-gradient-to-r from-green-400 via-green-500 to-green-400" 
              : "bg-gradient-to-r from-green-900 via-green-800 to-green-900"
            }
            bg-clip-text 
            text-transparent
            mb-6
            animate-gradient-x
          `}
        >
          {language === "en" ? "Our Expertise" : "Notre Expertise"}

          {/* Gradient Line with Increased Spacing */}
          <div
            className="w-24 h-1 mx-auto rounded-full mt-6 bg-gradient-to-r from-green-500/50 to-green-300/50"
          />
        </h2>

          </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className={`group rounded-3xl overflow-hidden border transition-all transform scale-95 hover:scale-100 service-card ${
                theme === "dark"
                  ? "bg-black border-green-500/90"
                  : "bg-white border-gray-300"
              }`}
            >
              <div className="aspect-video relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20 z-10" />
                <img
                  src={service.imageUrl}
                  alt={language === "en" ? service.titleEn : service.titleFr}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                />
                <div
                  className={`absolute top-4 left-4 z-20 w-10 h-10 rounded-lg flex items-center justify-center ${
                    theme === "dark"
                      ? "bg-green-500/30"
                      : "bg-green-100"
                  }`}
                >
                  <service.icon
                    className={`w-5 h-5 ${
                      theme === "dark" ? "text-white-500" : "text-green-600"
                    }`}
                  />
                </div>
              </div>
              <div className="p-4">
                <h3
                  className={`text-2xl font-semibold mb-6 pl-4 ${
                    theme === "dark" ? "text-white" : "text-gray-800"
                  }`}
                >
                  {language === "en" ? service.titleEn : service.titleFr}
                </h3>
                <ul className="space-y-2 pl-10"> {/* Increased padding-left for points */}
                  {(language === "en" ? service.points.en : service.points.fr).map(
                    (point, idx) => (
                      <li
                        key={idx}
                        className={`flex items-start text-lg ${
                          theme === "dark" ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        <span
                          className={`w-2 h-2 mt-2 mr-2 rounded-full ${
                            theme === "dark" ? "bg-green-500" : "bg-green-600"
                          }`}
                        />
                        {point}
                      </li>
                    )
                  )}
                </ul>
              </div>

            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;

